import React, { useCallback, useEffect, useState } from "react";
import {
  ShieldAlert, Search, Loader2, Building2, CalendarDays, Plus, Users,
  Ticket as TicketIcon, CheckCircle2, XCircle, FlaskConical, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { superAdminApi, centsToAUD, formatAuDate } from "../../lib/events";
import { extractError } from "../../lib/api";
import CreateBookingDialog from "../../components/superadmin/CreateBookingDialog";
import SalesStatusControl from "../../components/SalesStatusControl";
import DeleteDialog from "../../components/superadmin/DeleteDialog";
import BookingDrawer from "../../components/superadmin/BookingDrawer";

export default function SuperAdmin() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [orgQuery, setOrgQuery] = useState("");
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [overview, setOverview] = useState(null);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  const [tab, setTab] = useState("bookings");
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  const [showCreate, setShowCreate] = useState(false);
  const [drawerOrderId, setDrawerOrderId] = useState(null);
  const [del, setDel] = useState(null); // { mode, id }

  const loadOrgs = useCallback(async (q = "") => {
    setLoadingOrgs(true);
    try { setOrgs(await superAdminApi.organisers(q)); }
    catch (e) { toast.error(extractError(e)); }
    finally { setLoadingOrgs(false); }
  }, []);

  useEffect(() => { if (user?.is_admin) loadOrgs(""); }, [user, loadOrgs]);

  const pickOrg = async (org) => {
    setSelectedOrg(org); setEvents([]); setSelectedEventId(""); setOverview(null);
    try {
      const res = await superAdminApi.events(org.id);
      setEvents(res.events || []);
    } catch (e) { toast.error(extractError(e)); }
  };

  const pickEvent = useCallback(async (eventId) => {
    setSelectedEventId(eventId);
    setOverview(null);
    if (!eventId) return;
    try { setOverview(await superAdminApi.overview(eventId)); }
    catch (e) { toast.error(extractError(e)); }
  }, []);

  // reload overview when refreshKey changes (after mutations)
  useEffect(() => {
    if (selectedEventId) superAdminApi.overview(selectedEventId).then(setOverview).catch(() => {});
  }, [refreshKey, selectedEventId]);

  if (!user?.is_admin) {
    return (
      <div className="p-8" data-testid="sa-access-denied">
        <div className="max-w-md border-2 border-[var(--gt-accent)] bg-red-50 p-6">
          <XCircle className="text-[var(--gt-accent)] mb-2" />
          <h2 className="font-heading text-2xl font-black">Access denied</h2>
          <p className="text-sm text-[var(--gt-ink-2)] mt-1">This area is for platform super admins only.</p>
        </div>
      </div>
    );
  }

  const ticketTypes = overview?.ticket_types || [];
  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="super-admin-page">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-[var(--gt-accent)] text-white p-2 rounded-sm border-2 border-[var(--gt-ink)]"><ShieldAlert size={20} /></div>
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter leading-none">Platform Super Admin</h1>
          <p className="text-sm text-[var(--gt-ink-2)]">Manage tickets & attendees for any organiser and event.</p>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--gt-ink-3)] my-4" data-testid="sa-breadcrumb">
        <span className="font-bold text-[var(--gt-ink)]">Organisers</span>
        {selectedOrg && <><ChevronRight size={12} /><span>{selectedOrg.business_name || selectedOrg.name}</span></>}
        {overview && <><ChevronRight size={12} /><span>{overview.event.title}</span></>}
      </div>

      {overview && (
        <div className="border-2 border-[var(--gt-ink)] p-4 mb-6" data-testid="sa-sales-status">
          <div className="gt-label mb-2">Ticket sales</div>
          <SalesStatusControl scope="event" eventId={overview.event.id} contextLabel="Whole event"
            current={{ sales_closed: overview.event.sales_closed, sales_closed_label: overview.event.sales_closed_label, sales_closed_message: overview.event.sales_closed_message }}
            onChanged={() => setRefreshKey((k) => k + 1)} />
          <div className="mt-3 space-y-2">
            {ticketTypes.map((t) => (
              <div key={t.id} className="flex items-center gap-2 flex-wrap text-sm">
                <span className="font-bold min-w-[100px]">{t.name}</span>
                <SalesStatusControl scope="ticket_type" eventId={overview.event.id} ttId={t.id} contextLabel={t.name}
                  current={{ sales_closed: t.sales_closed, sales_closed_label: t.sales_closed_label, sales_closed_message: t.sales_closed_message }}
                  onChanged={() => setRefreshKey((k) => k + 1)} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 1: organiser */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-6 mb-6">
        <div className="border-2 border-[var(--gt-ink)] p-4">
          <div className="gt-label mb-2 flex items-center gap-1.5"><Building2 size={14} /> Select organiser</div>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--gt-ink-3)]" />
              <input className="gt-input pl-7 w-full" placeholder="Search name or email"
                value={orgQuery} onChange={(e) => setOrgQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") loadOrgs(orgQuery); }}
                data-testid="sa-org-search" />
            </div>
            <button className="gt-btn-ghost text-xs" onClick={() => loadOrgs(orgQuery)} data-testid="sa-org-search-btn">Search</button>
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-[var(--gt-line)]" data-testid="sa-org-list">
            {loadingOrgs ? (
              <div className="py-6 flex justify-center"><Loader2 className="animate-spin" size={18} /></div>
            ) : orgs.length === 0 ? (
              <div className="py-6 text-center text-xs text-[var(--gt-ink-3)]">No organisers.</div>
            ) : orgs.map((o) => (
              <button key={o.id} onClick={() => pickOrg(o)}
                className={`w-full text-left py-2 px-2 hover:bg-[var(--gt-line)]/40 ${selectedOrg?.id === o.id ? "bg-[var(--gt-line)]/60" : ""}`}
                data-testid={`sa-org-${o.id}`}>
                <div className="text-sm font-semibold truncate">{o.business_name || o.name}</div>
                <div className="text-[11px] text-[var(--gt-ink-3)] truncate">{o.email}{o.is_suspended ? " · suspended" : ""}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: event */}
        <div className="border-2 border-[var(--gt-ink)] p-4">
          <div className="gt-label mb-2 flex items-center gap-1.5"><CalendarDays size={14} /> Select event</div>
          {!selectedOrg ? (
            <div className="py-6 text-center text-xs text-[var(--gt-ink-3)]">Pick an organiser first.</div>
          ) : (
            <EventPicker
              organiserId={selectedOrg.id}
              initialEvents={events}
              selectedEventId={selectedEventId}
              selectedTitle={overview?.event?.title}
              onPick={pickEvent}
            />
          )}
          {overview && (
            <div className="grid grid-cols-5 gap-2 mt-4 text-center" data-testid="sa-overview-counts">
              <Stat icon={Users} label="Active" value={overview.counts.active} />
              <Stat icon={CheckCircle2} label="In" value={overview.counts.checked_in} />
              <Stat icon={XCircle} label="Void" value={overview.counts.voided} />
              <Stat icon={TicketIcon} label="Comp" value={overview.counts.complimentary} />
              <Stat icon={FlaskConical} label="Test" value={overview.counts.internal_test} />
            </div>
          )}
        </div>
      </div>

      {/* Step 3: bookings / tickets */}
      {overview && (
        <div data-testid="sa-manage-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 border-b-2 border-[var(--gt-ink)]">
              <TabBtn active={tab === "bookings"} onClick={() => setTab("bookings")} testid="sa-tab-bookings">Bookings</TabBtn>
              <TabBtn active={tab === "tickets"} onClick={() => setTab("tickets")} testid="sa-tab-tickets">Tickets</TabBtn>
            </div>
            <button className="gt-btn-primary" onClick={() => setShowCreate(true)} data-testid="sa-create-booking-btn">
              <Plus size={14} /> Create booking
            </button>
          </div>

          {tab === "bookings"
            ? <BookingsTab eventId={selectedEventId} refreshKey={refreshKey} onOpen={setDrawerOrderId} />
            : <TicketsTab eventId={selectedEventId} ticketTypes={ticketTypes} refreshKey={refreshKey} onOpen={setDrawerOrderId} onDelete={(id) => setDel({ mode: "ticket", id })} />}
        </div>
      )}

      <CreateBookingDialog
        open={showCreate} onClose={() => setShowCreate(false)}
        organiserId={selectedOrg?.id} eventId={selectedEventId} ticketTypes={ticketTypes}
        onCreated={() => bump()}
      />
      {drawerOrderId && (
        <BookingDrawer
          orderId={drawerOrderId} refreshKey={refreshKey}
          onClose={() => setDrawerOrderId(null)}
          onDeleteTicket={(id) => setDel({ mode: "ticket", id })}
          onDeleteBooking={(id) => setDel({ mode: "booking", id })}
          onMutated={bump}
        />
      )}
      {del && (
        <DeleteDialog
          open mode={del.mode} id={del.id}
          onClose={() => setDel(null)}
          onDone={() => { setDel(null); bump(); }}
        />
      )}
    </div>
  );
}

function BookingsTab({ eventId, refreshKey, onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [mode, setMode] = useState("");
  const [payment, setPayment] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setData(await superAdminApi.bookings(eventId, {
        q: q || undefined, status: status || undefined, source: source || undefined,
        record_mode: mode || undefined, payment: payment || undefined,
        include_internal_test: includeTest, page, per_page: 25,
      }));
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  }, [eventId, q, status, source, mode, payment, includeTest, page]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);
  useEffect(() => { setPage(1); }, [q, status, source, mode, payment, includeTest, eventId]);

  return (
    <div>
      <Filters>
        <SearchBox value={q} onChange={setQ} onEnter={fetchData} placeholder="Search buyer / email / booking / code" testid="sa-bookings-search" />
        <Sel value={status} onChange={setStatus} testid="sa-bookings-status" opts={[["", "All statuses"], ["paid", "paid"], ["complete", "complete"], ["partially_refunded", "partially refunded"], ["refunded", "refunded"], ["partially_cancelled", "partially cancelled"], ["cancelled", "cancelled"]]} />
        <Sel value={source} onChange={setSource} testid="sa-bookings-source" opts={[["", "All sources"], ["stripe", "stripe"], ["manual", "manual"], ["super_admin", "super_admin"]]} />
        <Sel value={mode} onChange={setMode} testid="sa-bookings-mode" opts={[["", "All modes"], ["MANUAL_REAL", "Manual real"], ["COMPLIMENTARY", "Complimentary"], ["INTERNAL_TEST", "Internal/test"]]} />
        <Sel value={payment} onChange={setPayment} testid="sa-bookings-payment" opts={[["", "Paid & free"], ["paid", "Paid"], ["free", "Free"]]} />
        <label className="flex items-center gap-1.5 text-xs whitespace-nowrap"><input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} data-testid="sa-bookings-include-test" /> incl. test</label>
      </Filters>

      {loading ? <Center><Loader2 className="animate-spin" /></Center> : (
        <div className="overflow-x-auto border-2 border-[var(--gt-ink)]">
          <table className="w-full text-sm" data-testid="sa-bookings-table">
            <thead className="bg-[var(--gt-bg-marketing)] text-left text-xs uppercase tracking-widest">
              <tr>
                <Th>Buyer</Th><Th>Tickets</Th><Th>Amount</Th><Th>Source</Th><Th>Status</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {data?.items?.length ? data.items.map((o) => (
                <tr key={o.id} className="border-t border-[var(--gt-line)] hover:bg-[var(--gt-line)]/30 cursor-pointer" onClick={() => onOpen(o.id)} data-testid={`sa-booking-row-${o.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold truncate max-w-[180px]">{o.buyer_name || "—"}</div>
                    <div className="text-[11px] text-[var(--gt-ink-3)] truncate max-w-[180px]">{o.buyer_email}</div>
                    {o.internal_test && <span className="text-[10px] text-[var(--gt-accent)] font-bold">TEST</span>}
                  </td>
                  <td className="px-3 py-2">{o.tickets_active}/{o.tickets_total}</td>
                  <td className="px-3 py-2">{o.amount_total_cents ? centsToAUD(o.amount_total_cents) : "Free"}{o.refunded_cents > 0 ? <span className="block text-[10px] text-amber-700">-{centsToAUD(o.refunded_cents)}</span> : null}</td>
                  <td className="px-3 py-2 text-xs">{o.order_source}</td>
                  <td className="px-3 py-2 text-xs">{o.cancellation_status || o.status}</td>
                  <td className="px-3 py-2 text-right"><ChevronRight size={14} className="text-[var(--gt-ink-3)]" /></td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="px-3 py-8 text-center text-xs text-[var(--gt-ink-3)]" data-testid="sa-bookings-empty">No bookings.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pager data={data} page={page} setPage={setPage} />
    </div>
  );
}

function TicketsTab({ eventId, ticketTypes, refreshKey, onOpen, onDelete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [ticketStatus, setTicketStatus] = useState("");
  const [ttId, setTtId] = useState("");
  const [mode, setMode] = useState("");
  const [includeTest, setIncludeTest] = useState(false);
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setData(await superAdminApi.tickets(eventId, {
        q: q || undefined, ticket_status: ticketStatus || undefined,
        ticket_type_id: ttId || undefined, record_mode: mode || undefined,
        include_internal_test: includeTest, page, per_page: 50,
      }));
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  }, [eventId, q, ticketStatus, ttId, mode, includeTest, page]);

  useEffect(() => { fetchData(); }, [fetchData, refreshKey]);
  useEffect(() => { setPage(1); }, [q, ticketStatus, ttId, mode, includeTest, eventId]);

  return (
    <div>
      <Filters>
        <SearchBox value={q} onChange={setQ} onEnter={fetchData} placeholder="Search code / attendee / email" testid="sa-tickets-search" />
        <Sel value={ticketStatus} onChange={setTicketStatus} testid="sa-tickets-status" opts={[["", "All statuses"], ["valid", "valid"], ["checked_in", "checked in"], ["voided", "voided"], ["cancelled", "cancelled"], ["refunded", "refunded"]]} />
        <Sel value={ttId} onChange={setTtId} testid="sa-tickets-type" opts={[["", "All types"], ...ticketTypes.map((t) => [t.id, t.name])]} />
        <Sel value={mode} onChange={setMode} testid="sa-tickets-mode" opts={[["", "All modes"], ["MANUAL_REAL", "Manual real"], ["COMPLIMENTARY", "Complimentary"], ["INTERNAL_TEST", "Internal/test"]]} />
        <label className="flex items-center gap-1.5 text-xs whitespace-nowrap"><input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} data-testid="sa-tickets-include-test" /> incl. test</label>
      </Filters>

      {loading ? <Center><Loader2 className="animate-spin" /></Center> : (
        <div className="overflow-x-auto border-2 border-[var(--gt-ink)]">
          <table className="w-full text-sm" data-testid="sa-tickets-table">
            <thead className="bg-[var(--gt-bg-marketing)] text-left text-xs uppercase tracking-widest">
              <tr><Th>Attendee</Th><Th>Type</Th><Th>Code</Th><Th>Status</Th><Th></Th></tr>
            </thead>
            <tbody>
              {data?.items?.length ? data.items.map((t) => {
                const dead = ["voided", "cancelled", "refunded"].includes(t.status);
                return (
                  <tr key={t.id} className="border-t border-[var(--gt-line)] hover:bg-[var(--gt-line)]/30" data-testid={`sa-ticket-list-row-${t.code}`}>
                    <td className="px-3 py-2">
                      <div className="font-semibold truncate max-w-[180px]">{t.attendee_name || "—"}</div>
                      <div className="text-[11px] text-[var(--gt-ink-3)] truncate max-w-[180px]">{t.attendee_email}</div>
                      {t.internal_test && <span className="text-[10px] text-[var(--gt-accent)] font-bold">TEST</span>}
                    </td>
                    <td className="px-3 py-2 text-xs">{t.ticket_type_name}</td>
                    <td className="px-3 py-2 font-mono text-xs">{t.code}</td>
                    <td className="px-3 py-2 text-xs">{t.status}{t.voided_after_checkin ? " *" : ""}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button className="gt-btn-ghost text-xs mr-1" onClick={() => onOpen(t.order_id)} data-testid={`sa-ticket-open-${t.code}`}>Booking</button>
                      {!dead && (
                        <button className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm bg-[var(--gt-accent)] text-white font-bold border border-[var(--gt-ink)]"
                          onClick={() => onDelete(t.id)} data-testid={`sa-ticket-list-delete-${t.code}`}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-xs text-[var(--gt-ink-3)]" data-testid="sa-tickets-empty">No tickets.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pager data={data} page={page} setPage={setPage} />
    </div>
  );
}

/* ---- searchable event picker (handles organisers with 100s of events) ---- */
function EventPicker({ organiserId, initialEvents, selectedEventId, selectedTitle, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(initialEvents || []);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setResults(initialEvents || []); }, [initialEvents]);

  useEffect(() => {
    if (!organiserId) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await superAdminApi.events(organiserId, { q: q || undefined, limit: 50 });
        setResults(res.events || []);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, organiserId]);

  return (
    <div className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--gt-ink-3)]" />
        <input
          className="gt-input pl-7 w-full"
          placeholder={selectedTitle ? `Selected: ${selectedTitle}` : "Search events by title…"}
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          data-testid="sa-event-search"
        />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border-2 border-[var(--gt-ink)] max-h-64 overflow-y-auto shadow-[4px_4px_0_0_rgba(0,0,0,0.15)]" data-testid="sa-event-options">
          {loading ? (
            <div className="py-4 flex justify-center"><Loader2 className="animate-spin" size={16} /></div>
          ) : results.length === 0 ? (
            <div className="py-4 text-center text-xs text-[var(--gt-ink-3)]">No events.</div>
          ) : results.map((e) => (
            <button key={e.id} type="button"
              onClick={() => { onPick(e.id); setOpen(false); setQ(""); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--gt-line)]/40 border-b border-[var(--gt-line)] last:border-0 ${selectedEventId === e.id ? "bg-[var(--gt-line)]/60" : ""}`}
              data-testid={`sa-event-option-${e.id}`}>
              <div className="font-semibold truncate">{e.title}</div>
              <div className="text-[11px] text-[var(--gt-ink-3)]">{e.status}{e.start_at ? ` · ${formatAuDate(e.start_at)}` : ""}</div>
            </button>
          ))}
        </div>
      )}
      {open && <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />}
    </div>
  );
}

/* ---- small presentational helpers ---- */
function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-[var(--gt-line)] p-2">
      <Icon size={14} className="mx-auto text-[var(--gt-ink-3)]" />
      <div className="font-heading text-lg font-black leading-none mt-1">{value ?? 0}</div>
      <div className="text-[9px] uppercase tracking-widest text-[var(--gt-ink-3)]">{label}</div>
    </div>
  );
}
function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-0.5 ${active ? "border-[var(--gt-accent)] text-[var(--gt-ink)]" : "border-transparent text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)]"}`}>
      {children}
    </button>
  );
}
function Filters({ children }) {
  return <div className="flex flex-wrap items-center gap-2 mb-3">{children}</div>;
}
function SearchBox({ value, onChange, onEnter, placeholder, testid }) {
  return (
    <div className="relative flex-1 min-w-[200px]">
      <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--gt-ink-3)]" />
      <input className="gt-input pl-7 w-full py-1.5 text-sm" placeholder={placeholder} value={value}
        onChange={(e) => onChange(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") onEnter(); }} data-testid={testid} />
    </div>
  );
}
function Sel({ value, onChange, opts, testid }) {
  return (
    <select className="gt-input py-1.5 text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
      {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}
function Th({ children }) { return <th className="px-3 py-2 font-bold">{children}</th>; }
function Center({ children }) { return <div className="py-10 flex justify-center">{children}</div>; }
function Pager({ data, page, setPage }) {
  if (!data || (data.pages || 1) <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-3 text-sm" data-testid="sa-pager">
      <span className="text-xs text-[var(--gt-ink-3)]">Page {data.page} of {data.pages} · {data.total} total</span>
      <div className="flex gap-2">
        <button className="gt-btn-ghost text-xs" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="sa-pager-prev">Prev</button>
        <button className="gt-btn-ghost text-xs" disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)} data-testid="sa-pager-next">Next</button>
      </div>
    </div>
  );
}
